import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitLead } from "@/lib/api";
import { getUTMs } from "@/lib/utm";
import { trackEvent } from "@/lib/analytics";

const TRAVELLERS = ["2", "3", "4", "5", "6", "7", "8", "9", "10+"];

const EnquiryForm = ({ packageName = null, source = "unknown", onSuccess, testIdPrefix = "enquiry" }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", phone: "", travellers: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);

  const markStart = () => {
    if (!started) {
      setStarted(true);
      trackEvent("enquiry_form_start", { source, package: packageName || undefined });
    }
  };

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name";
    const digits = form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
    if (!/^[6-9]\d{9}$/.test(digits)) e.phone = "Enter a valid 10-digit mobile number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await submitLead({
        ...form,
        travellers: form.travellers || null,
        package_name: packageName || null,
        source,
        utm: getUTMs(),
      });
      trackEvent("enquiry_form_submit", { source, package: packageName || undefined });
      trackEvent("generate_lead", { source, package: packageName || undefined });
      toast.success(res.message || "Thank you! Our Kashmir travel expert will contact you shortly.");
      const submittedName = form.name;
      setForm({ name: "", phone: "", travellers: "" });
      onSuccess && onSuccess();
      navigate("/thank-you", { state: { name: submittedName, packageName } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try WhatsApp or call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5" data-testid={`${testIdPrefix}-form`} noValidate>
      {packageName && (
        <div className="rounded-md bg-[#E3EAE4] px-3 py-2 text-xs font-medium text-[#0F2F24]" data-testid={`${testIdPrefix}-package-label`}>
          Enquiring for: <span className="font-semibold">{packageName}</span>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-name`}>Full Name *</Label>
        <Input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name-input`} placeholder="Your name" value={form.name}
          onFocus={markStart} onChange={(e) => set("name", e.target.value)} className={errors.name ? "border-red-500" : ""} />
        {errors.name && <p className="text-xs text-red-600" data-testid={`${testIdPrefix}-name-error`}>{errors.name}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-phone`}>Mobile Number *</Label>
        <Input id={`${testIdPrefix}-phone`} data-testid={`${testIdPrefix}-phone-input`} type="tel" inputMode="numeric" placeholder="98765 43210" value={form.phone}
          onFocus={markStart} onChange={(e) => set("phone", e.target.value)} className={errors.phone ? "border-red-500" : ""} />
        {errors.phone && <p className="text-xs text-red-600" data-testid={`${testIdPrefix}-phone-error`}>{errors.phone}</p>}
      </div>
      <div className="space-y-1.5">
        <Label>Number of Travellers</Label>
        <Select value={form.travellers} onValueChange={(v) => { markStart(); set("travellers", v); }}>
          <SelectTrigger data-testid={`${testIdPrefix}-travellers-select`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{TRAVELLERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={loading} data-testid={`${testIdPrefix}-submit-button`}
        className="w-full bg-[#D99B26] text-[#0F2F24] hover:bg-[#c98f22] font-semibold text-base h-12 rounded-md transition-colors">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Get My Kashmir Package"}
      </Button>
      <p className="text-[11px] text-center text-slate-500">No spam, no obligation — just a personalised Kashmir quote from a real travel expert.</p>
    </form>
  );
};

export default EnquiryForm;
